import { type DependencyList, useEffect } from 'react';

import { isEmpty, isEqual, uniq } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetPositionList, doGetQuoteList } from 'src/actions';
import { Position } from 'src/models';
import { Maybe } from 'src/utils';

import { CustomHookBaseOptions } from '../models/common.model';

import { useAccountSelector } from './useAccountSelector';
import { usePositionListSelector, UsePositionListSelectorReturnType } from './usePositionListSelector';

interface UsePositionsReturnType extends UsePositionListSelectorReturnType {}

export interface UsePositionsOptions extends CustomHookBaseOptions {
  skipQuotes?: boolean;
  refetchOnChange?: boolean;
  dependency?: DependencyList;
}

export const usePositions = ({
  skip,
  skipQuotes,
  refetchOnChange,
  dependency,
}: UsePositionsOptions): UsePositionsReturnType => {
  const dispatch = useDispatch();

  const positionListRaw: Maybe<Position[]> = useSelector((state: any) => state.positions.list.data?.data);
  const {
    hasPositionListError,
    isPositionListLoading,
    nonTradablePositionList,
    positionList,
    tradablePositionList,
    positionListError,
  } = usePositionListSelector();
  const payload = useSelector((state: any) => state.positions.list?.payload);
  const isQuoteListFetched: boolean = useSelector((state: any) => Boolean(state.quotes.list?.__isFetched));
  const hasQuoteListError: boolean = useSelector((state: any) => Boolean(state.quotes.list?.__failed));

  const { account } = useAccountSelector();

  const fetchList = () => {
    if (!account?.accountId) {
      return;
    }

    dispatch(doGetPositionList({ params: { accountId: account.accountId } }));
  };

  useEffect(() => {
    if (skipQuotes) {
      return;
    }

    if (isEmpty(tradablePositionList)) {
      return;
    }

    if (isQuoteListFetched && !hasQuoteListError) {
      return;
    }

    const symbols = tradablePositionList.map(aTradablePosition => aTradablePosition.symbol);

    if (isEmpty(uniq(symbols))) {
      return;
    }

    dispatch(doGetQuoteList({ queryParams: { symbols: uniq(symbols).join(',') } }));
  }, [skipQuotes, tradablePositionList]);

  useEffect(() => {
    if (skip) {
      return;
    }

    if (!positionListRaw && !isPositionListLoading && !hasPositionListError) {
      fetchList();

      return;
    }

    if (refetchOnChange || !isEqual(payload, { params: { accountId: account?.accountId } })) {
      fetchList();
    }
  }, dependency);

  return {
    positionList,
    isPositionListLoading,
    hasPositionListError: hasPositionListError || hasQuoteListError, // TODO: separate quote list error and position list error
    positionListError,
    tradablePositionList,
    nonTradablePositionList,
  };
};
